/**
 * 계약의뢰 첨부파일 (API cm_app_file / IndexedDB 폴백)
 */
import { isBdApiEnabled, FILE_BUCKET } from 'services/pocRowPersist';
import { uploadAppFile, downloadAppFile, deleteAppFile } from 'services/appFileApi';

const DB_NAME = 'erp.expense.contractRequest.files';
const DB_VERSION = 1;
const STORE = 'files';

/** 업로드 허용 (열람 불가 포함 — 열람은 VIEWABLE만) */
export const CONTRACT_REQUEST_UPLOAD_ACCEPT =
  '.pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.hwp,.hwpx,.doc,.docx,.xls,.xlsx';

/** 인앱 뷰어 지원 확장자 */
export const CONTRACT_REQUEST_VIEWABLE_EXTS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'txt',
  'hwp',
  'hwpx',
];

/** PoC 용량 상한 (바이트) */
export const CONTRACT_REQUEST_FILE_MAX_BYTES = 8 * 1024 * 1024;

export function getFileExt(fileName = '') {
  const name = String(fileName || '').trim();
  const i = name.lastIndexOf('.');
  if (i < 0) return '';
  return name.slice(i + 1).toLowerCase();
}

export function isViewableExt(extOrFileName) {
  const ext = String(extOrFileName || '')
    .replace(/^\./, '')
    .toLowerCase();
  const normalized = ext.includes('.') ? getFileExt(ext) : ext;
  return CONTRACT_REQUEST_VIEWABLE_EXTS.includes(normalized);
}

export function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB를 사용할 수 없습니다.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'fileKey' });
      }
    };
  });
}

function idbReq(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
  });
}

/**
 * @typedef {{
 *   fileKey: string,
 *   docUid: string,
 *   requestUid: string,
 *   fileName: string,
 *   mimeType: string,
 *   fileSize: number,
 *   fileExt: string,
 *   blob: Blob,
 *   updatedAt: string,
 * }} ContractRequestFileRecord
 */

/**
 * @param {string} docUid
 * @param {{ requestUid: string, file: File|Blob, fileName?: string }} payload
 */
export async function saveContractRequestFile(docUid, payload) {
  const uid = String(docUid || '').trim();
  if (!uid) throw new Error('docUid가 필요합니다.');
  const file = payload?.file;
  if (!file) throw new Error('파일이 없습니다.');

  const fileName = String(payload.fileName || file.name || 'file').trim();
  const fileExt = getFileExt(fileName);
  const fileSize = Number(file.size) || 0;
  if (fileSize > CONTRACT_REQUEST_FILE_MAX_BYTES) {
    throw new Error(
      `파일 용량은 ${formatFileSize(CONTRACT_REQUEST_FILE_MAX_BYTES)} 이하여야 합니다.`
    );
  }

  const blob =
    file instanceof Blob
      ? file
      : new Blob([file], { type: file.type || 'application/octet-stream' });

  if (isBdApiEnabled()) {
    await uploadAppFile({
      bucket: FILE_BUCKET.CONTRACT_REQUEST,
      objectKey: uid,
      file: blob,
      fileName,
    });
    return {
      docUid: uid,
      fileName,
      mimeType: blob.type || file.type || 'application/octet-stream',
      fileSize,
      fileExt,
    };
  }

  /** @type {ContractRequestFileRecord} */
  const record = {
    fileKey: uid,
    docUid: uid,
    requestUid: String(payload.requestUid || '').trim(),
    fileName,
    mimeType: blob.type || file.type || 'application/octet-stream',
    fileSize,
    fileExt,
    blob,
    updatedAt: new Date().toISOString(),
  };

  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await idbReq(tx.objectStore(STORE).put(record));
  } finally {
    db.close();
  }

  return {
    docUid: uid,
    fileName: record.fileName,
    mimeType: record.mimeType,
    fileSize: record.fileSize,
    fileExt: record.fileExt,
    viewableYn: isViewableExt(record.fileExt) ? 'Y' : 'N',
    hasFile: 'Y',
  };
}

/** @param {string} docUid */
export async function loadContractRequestFile(docUid) {
  const uid = String(docUid || '').trim();
  if (!uid) return null;
  if (isBdApiEnabled()) {
    const blob = await downloadAppFile(FILE_BUCKET.CONTRACT_REQUEST, uid);
    if (!blob) return null;
    return {
      fileKey: uid,
      docUid: uid,
      fileName: 'file',
      mimeType: blob.type || 'application/octet-stream',
      fileSize: blob.size,
      fileExt: getFileExt('file'),
      blob,
      updatedAt: '',
    };
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    const row = await idbReq(tx.objectStore(STORE).get(uid));
    return row || null;
  } finally {
    db.close();
  }
}

/** @param {string} docUid */
export async function deleteContractRequestFile(docUid) {
  const uid = String(docUid || '').trim();
  if (!uid) return;
  if (isBdApiEnabled()) {
    await deleteAppFile(FILE_BUCKET.CONTRACT_REQUEST, uid);
    return;
  }
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await idbReq(tx.objectStore(STORE).delete(uid));
  } finally {
    db.close();
  }
}

/** @param {string[]} docUids */
export async function deleteContractRequestFiles(docUids = []) {
  const ids = (docUids || []).map((id) => String(id || '').trim()).filter(Boolean);
  if (!ids.length) return;
  await Promise.all(ids.map((id) => deleteContractRequestFile(id)));
}

/**
 * File → 그리드 메타 필드
 * @param {File} file
 */
export function buildDocMetaFromFile(file) {
  const fileName = String(file?.name || '').trim();
  const fileExt = getFileExt(fileName);
  return {
    fileName,
    mimeType: file?.type || 'application/octet-stream',
    fileSize: Number(file?.size) || 0,
    fileExt,
    viewableYn: isViewableExt(fileExt) ? 'Y' : 'N',
    hasFile: 'Y',
  };
}
